import type { PlanCard, SubscriptionState } from '@/types';
import { usePlanStore } from '@/stores/planStore';
import { isSubscriptionActive } from './subscription';

/**
 * Where the user clicked "Upgrade plan", and what plan they had then.
 *
 * Stripe returns to the one fixed `BILLING_SUCCESS_URL`, so the route is ours
 * to remember (same reason as `socialOAuthRedirect`). Session storage: per-tab
 * and survives the full-page round trip to Checkout.
 */
const KEY = 'billing:upgrade-return';
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

export interface PlanSnapshot {
  planId: string | null;
  isLifetime: boolean;
  active: boolean;
}

export interface UpgradeReturn {
  route: string;
  conversationId?: string;
  from: PlanSnapshot;
  at: number;
}

export const snapshotPlan = (subscription: SubscriptionState | null): PlanSnapshot => ({
  planId: subscription?.planId ?? null,
  isLifetime: !!subscription?.isLifetime,
  active: isSubscriptionActive(subscription),
});

export function rememberUpgradeReturn(marker: Omit<UpgradeReturn, 'at'>, now = Date.now()): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...marker, at: now }));
  } catch {
    // Non-fatal: the user just lands on the checkout success page.
  }
}

function read(now: number): UpgradeReturn | null {
  try {
    const marker = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as UpgradeReturn | null;
    if (!marker || now - marker.at > MAX_AGE_MS || !marker.route?.startsWith('/app/')) return null;
    return marker;
  } catch {
    return null;
  }
}

// Peek (checkout landing) vs take (chat): the landing only redirects; the chat
// consumes the marker once the upgrade notice is actually sent.
export function peekUpgradeReturn(conversationId: string, now = Date.now()): UpgradeReturn | null {
  const marker = read(now);
  return marker?.conversationId === conversationId ? marker : null;
}

export function clearUpgradeReturn(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Already unreadable, so it can't be replayed.
  }
}

export function takeUpgradeReturn(conversationId: string, now = Date.now()): UpgradeReturn | null {
  const marker = peekUpgradeReturn(conversationId, now);
  if (marker) clearUpgradeReturn();
  return marker;
}

/**
 * Active alone is no proof: a trial user is active before buying Lifetime. A
 * plan switch counts only when the catalog prices it higher, so a downgrade
 * through the billing portal never tells LiLi to retry.
 */
export function isUpgraded(
  from: PlanSnapshot,
  now: SubscriptionState | null,
  catalog: PlanCard[] = []
): boolean {
  if (!now || !isSubscriptionActive(now)) return false;
  if (!from.active || (now.isLifetime && !from.isLifetime)) return true;
  if (now.planId === from.planId) return false;
  const priceOf = (id: string | null) => catalog.find(p => p.id === id)?.price.amount;
  const before = priceOf(from.planId);
  const after = priceOf(now.planId);
  return before === undefined || after === undefined ? true : after > before;
}

type Navigate = (to: string, options?: { replace: boolean }) => void;

/**
 * Back to where "Upgrade plan" was clicked, but only once the plan reads
 * upgraded vs. the snapshot: a portal return or an unrelated checkout lands on
 * the same success URL and must stay put.
 */
export async function returnAfterUpgrade(
  navigate: Navigate,
  isCancelled: () => boolean = () => false
): Promise<boolean> {
  const marker = read(Date.now());
  if (!marker || isCancelled()) return false;
  const store = usePlanStore.getState;
  const done = (s: SubscriptionState | null) => isUpgraded(marker.from, s, store().catalog);
  const upgraded = done(store().subscription) || (await store().pollUntil(done, { isCancelled }));
  if (!upgraded || isCancelled()) return false;
  navigate(marker.route, { replace: true });
  return true;
}

// The repo's default BILLING_CANCEL_URL says `cancelled`; older configs say `cancel`.
const CANCELLED_STATUSES: ReadonlySet<string> = new Set(['cancel', 'cancelled']);

export const isCheckoutCancel = (status: string | null): boolean =>
  status !== null && CANCELLED_STATUSES.has(status);

/** A `?status=` landing: forget a cancelled checkout, follow a paid one back. */
export async function settleCheckoutReturn(
  status: string | null,
  navigate: Navigate,
  isCancelled: () => boolean = () => false
): Promise<void> {
  if (isCheckoutCancel(status)) {
    clearUpgradeReturn();
    return;
  }
  if (status !== 'success') return;
  const active = await usePlanStore.getState().pollUntilActive({ isCancelled });
  if (active && !isCancelled()) await returnAfterUpgrade(navigate, isCancelled);
}

/** Leaving the automation page: only a checkout return goes back to the chat. */
export async function leaveCheckoutLanding(
  fromCheckout: boolean,
  navigate: Navigate
): Promise<void> {
  if (fromCheckout && (await returnAfterUpgrade(navigate))) return;
  navigate('/app/dashboard');
}
